import { LifeInsuredPerson } from '@app/domain/model/LifeInsuredPerson';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { BeneficiaryFormState } from '@app/domain/model/policy/BeneficiaryFormState';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ProfileOption } from '@app/domain/model/ProfileOption';
import { LegalHeirSelector } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/_sections/steps/BeneficiariesStep/LegalHeirSelector';
import { BeneficiaryEditView } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/_sections/steps/BeneficiariesStep/BeneficiaryEditView';
import { BeneficiaryListView } from '@app/app/self-service/(wizard)/change-beneficiary-death/[id]/_sections/steps/BeneficiariesStep/BeneficiaryListView';

interface Props {
    selectedInsuredPerson: Nullable<LifeInsuredPerson>;
    isMinor: boolean;
    legalHeirsDefault?: boolean;
    isLegalHeirSelected: boolean;
    handleLegalHeirChange: (event: ChangeEvent<HTMLInputElement>) => void;
    isEditingBeneficiary: boolean;
    existingBeneficiaries: BeneficiaryData[];
    newBeneficiaries: BeneficiaryData[];
    beneficiaryFormState: BeneficiaryFormState;
    isCombinedShareFull: boolean;
    canAddMoreBeneficiaries: boolean;
    handleAddBeneficiary: () => void;
    editingBeneficiary: Nullable<BeneficiaryData>;
    saveAttempted: boolean;
    editingBeneficiaryId: Nullable<string>;
    handleEditBeneficiary: (beneficiaryId: string) => void;
    isEstonianEshop?: boolean;
    beneficiaryErrors?: string[];
    handleSetEditingBeneficiary: (isEditing: boolean) => void;
    countryOptions: ProfileOption[];
}

export function BeneficiariesStep({
    selectedInsuredPerson,
    isMinor,
    isLegalHeirSelected,
    handleLegalHeirChange,
    isEditingBeneficiary,
    existingBeneficiaries,
    newBeneficiaries,
    beneficiaryFormState,
    isCombinedShareFull,
    canAddMoreBeneficiaries,
    handleAddBeneficiary,
    editingBeneficiary,
    saveAttempted,
    editingBeneficiaryId,
    handleEditBeneficiary,
    isEstonianEshop = false,
    beneficiaryErrors,
    handleSetEditingBeneficiary,
    countryOptions,
}: Props) {
    const shouldShowLegalHeirSelector = !isEstonianEshop;
    const canManageBeneficiaries = isEstonianEshop || (!isLegalHeirSelected && !isMinor);
    const { onShareChange: handleShareChange } = beneficiaryFormState;

    const [localShareValue, setLocalShareValue] = useState<string>('');

    useEffect(() => {
        setLocalShareValue('');
    }, [editingBeneficiaryId, isEditingBeneficiary]);

    const handleSetShare = useCallback(
        (share: string, id: string) => {
            setLocalShareValue(share);
            handleShareChange(id, share);
        },
        [handleShareChange],
    );

    const editingBeneficiaryValue = !canManageBeneficiaries ? false : isEditingBeneficiary;

    useChangeEffect(() => {
        handleSetEditingBeneficiary(editingBeneficiaryValue);
    }, editingBeneficiaryValue);

    if (!selectedInsuredPerson) {
        return null;
    }

    return (
        <div className="flex flex-col gap-6">
            {beneficiaryErrors && beneficiaryErrors?.length > 0 && (
                <div className="flex flex-col gap-2">
                    <Paragraph size="small" className="text-red-500">
                        {beneficiaryErrors[0]}
                    </Paragraph>
                </div>
            )}
            {shouldShowLegalHeirSelector && (
                <LegalHeirSelector
                    isMinor={isMinor}
                    isLegalHeirSelected={isLegalHeirSelected}
                    onLegalHeirChange={handleLegalHeirChange}
                />
            )}

            {canManageBeneficiaries &&
                (isEditingBeneficiary ? (
                    <BeneficiaryEditView
                        existingBeneficiaries={existingBeneficiaries}
                        newBeneficiaries={newBeneficiaries}
                        editingBeneficiary={editingBeneficiary ?? null}
                        saveAttempted={saveAttempted}
                        editingBeneficiaryId={editingBeneficiaryId ?? ''}
                        beneficiaryFormState={beneficiaryFormState}
                        isEstonianEshop={isEstonianEshop}
                        handleSetShare={handleSetShare}
                        shareValue={localShareValue}
                        countryOptions={countryOptions}
                    />
                ) : (
                    <BeneficiaryListView
                        existingBeneficiaries={existingBeneficiaries}
                        newBeneficiaries={newBeneficiaries}
                        isCombinedShareFull={isCombinedShareFull}
                        canAddMoreBeneficiaries={canAddMoreBeneficiaries}
                        handleAddBeneficiary={handleAddBeneficiary}
                        removeBeneficiary={beneficiaryFormState.removeBeneficiary}
                        handleEditBeneficiary={handleEditBeneficiary}
                    />
                ))}
        </div>
    );
}
